import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import GroupsIcon from '@mui/icons-material/Groups';
import InsightsIcon from '@mui/icons-material/Insights';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShieldIcon from '@mui/icons-material/Shield';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Button, Container, Grid, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import teamImage from '../images/banner-meeting-2b.jpg';
import './About.css';

const ACCENT = '#ef6a1d';
const ACCENT_HOVER = '#d85a10';
const NAVY = '#0b2447';

const differentiators = [
  {
    Icon: VerifiedIcon,
    title: 'SEBI Registered',
    desc: 'Compliant Research Analyst under SEBI regulations.',
  },
  {
    Icon: InsightsIcon,
    title: 'Research-led Calls',
    desc: 'Every recommendation backed by deep technical & fundamental analysis.',
  },
  {
    Icon: ShieldIcon,
    title: 'Risk-managed Approach',
    desc: 'Disciplined entry, target and stop-loss on every trade.',
  },
];

const About = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 8, md: 12 },
        overflow: 'hidden',
        /* Multi-layer background:
           1. soft base gradient
           2. mesh of radial orbs in brand colors
           3. fine dot pattern overlay */
        background: `
          radial-gradient(900px 600px at 10% 15%, rgba(239,106,29,0.14) 0%, rgba(239,106,29,0) 60%),
          radial-gradient(800px 500px at 90% 80%, rgba(59,130,246,0.13) 0%, rgba(59,130,246,0) 60%),
          radial-gradient(700px 500px at 90% 10%, rgba(11,36,71,0.1) 0%, rgba(11,36,71,0) 65%),
          linear-gradient(180deg, #fbfcfe 0%, #f1f5f9 60%, #ffffff 100%)
        `,
      }}
    >
      {/* Fine dot pattern overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle, rgba(11,36,71,0.08) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage:
            'radial-gradient(ellipse at center, #000 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, #000 30%, transparent 80%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Decorative animated blobs */}
      <Box
        component={motion.div}
        animate={{ y: [0, -20, 0], x: [0, 10, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          top: 80,
          right: '8%',
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: ACCENT,
          opacity: 0.4,
          boxShadow: `0 0 30px ${ACCENT}`,
          zIndex: 0,
        }}
      />
      <Box
        component={motion.div}
        animate={{ y: [0, 20, 0], x: [0, -8, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          bottom: 120,
          left: '5%',
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: '#3b82f6',
          opacity: 0.5,
          boxShadow: '0 0 24px #3b82f6',
          zIndex: 0,
        }}
      />
      <Box
        component={motion.div}
        animate={{ y: [0, -16, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        sx={{
          position: 'absolute',
          top: '40%',
          left: '3%',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: NAVY,
          opacity: 0.3,
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          {/* Image column */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7 }}
            >
              <Box
                sx={{
                  position: 'relative',
                  pr: { xs: 0, md: 7 },
                  pb: { xs: 6, md: 9 },
                  pl: { xs: 0, md: 2 },
                  pt: { xs: 0, md: 3 },
                }}
              >
                {/* Tilted accent gradient shape behind image */}
                <Box
                  component={motion.div}
                  animate={{ rotate: [-3, -1, -3] }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  sx={{
                    position: 'absolute',
                    top: 28,
                    left: -14,
                    right: 32,
                    bottom: 56,
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${ACCENT} 0%, #f59e0b 100%)`,
                    boxShadow: '0 30px 60px -20px rgba(239,106,29,0.55)',
                    zIndex: 0,
                  }}
                />

                {/* Dotted accent in the top-left corner */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: -8,
                    width: 100,
                    height: 100,
                    backgroundImage:
                      'radial-gradient(circle, rgba(11,36,71,0.45) 1.5px, transparent 1.5px)',
                    backgroundSize: '14px 14px',
                    zIndex: 0,
                  }}
                />

                {/* Main image card */}
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    height: { xs: 360, sm: 440, md: 520 },
                    boxShadow: '0 30px 60px -15px rgba(11,36,71,0.4)',
                    zIndex: 1,
                    border: '6px solid #fff',
                  }}
                >
                  <Box
                    component="img"
                    src={teamImage}
                    alt="Real Stock Experts team"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(180deg, rgba(11,36,71,0.12) 0%, rgba(11,36,71,0.4) 100%)',
                    }}
                  />

                  {/* SEBI badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      left: 20,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.8,
                      px: 1.4,
                      py: 0.5,
                      borderRadius: 5,
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      color: '#fff',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      letterSpacing: 0.8,
                    }}
                  >
                    <VerifiedIcon sx={{ fontSize: '0.9rem' }} />
                    SEBI REGISTERED
                  </Box>

                  {/* Location pill */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 20,
                      left: 20,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      px: 1.4,
                      py: 0.5,
                      borderRadius: 5,
                      background: 'rgba(6,26,54,0.65)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      color: '#fff',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                    }}
                  >
                    <LocationOnIcon
                      sx={{ fontSize: '0.95rem', color: ACCENT }}
                    />
                    Mumbai, India
                  </Box>
                </Box>

                {/* Floating glass card — Top-right: client retention */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  style={{
                    position: 'absolute',
                    top: 40,
                    right: 0,
                    zIndex: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'flex' },
                      alignItems: 'center',
                      gap: 1.4,
                      px: 2,
                      py: 1.5,
                      borderRadius: 2.5,
                      background:
                        'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
                      backdropFilter: 'blur(14px)',
                      boxShadow:
                        '0 24px 50px -15px rgba(11,36,71,0.35), inset 0 1px 0 rgba(255,255,255,0.7)',
                      border: '1px solid rgba(255,255,255,0.6)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        background:
                          'linear-gradient(135deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0.05) 100%)',
                        color: '#16a34a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <TrendingUpIcon />
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          fontSize: '1.25rem',
                          fontWeight: 800,
                          color: NAVY,
                          lineHeight: 1,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        95%
                      </Box>
                      <Box
                        sx={{
                          fontSize: '0.74rem',
                          color: '#64748b',
                          fontWeight: 600,
                        }}
                      >
                        Client Retention
                      </Box>
                    </Box>
                  </Box>
                </motion.div>

                {/* Floating chart card — bottom-left overlap */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: 0.45 }}
                  style={{
                    position: 'absolute',
                    bottom: 30,
                    left: -10,
                    zIndex: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      px: 2,
                      py: 1.6,
                      borderRadius: 2.5,
                      background: NAVY,
                      color: '#fff',
                      boxShadow:
                        '0 24px 50px -15px rgba(11,36,71,0.45)',
                      minWidth: 200,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 80,
                        height: 80,
                        background:
                          'radial-gradient(circle, rgba(239,106,29,0.35) 0%, rgba(239,106,29,0) 70%)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.6,
                        mb: 0.4,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: ACCENT,
                        letterSpacing: 0.8,
                        position: 'relative',
                      }}
                    >
                      <AutoGraphIcon sx={{ fontSize: '0.85rem' }} />
                      MARKET INSIGHTS
                    </Box>
                    {/* Mini sparkline */}
                    <Box
                      component="svg"
                      viewBox="0 0 100 24"
                      sx={{ width: '100%', height: 24, position: 'relative' }}
                    >
                      <defs>
                        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={ACCENT} stopOpacity="0.5" />
                          <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M0 18 L12 14 L24 16 L36 8 L48 12 L60 6 L72 9 L84 4 L100 2 L100 24 L0 24 Z"
                        fill="url(#sparkFill)"
                      />
                      <path
                        d="M0 18 L12 14 L24 16 L36 8 L48 12 L60 6 L72 9 L84 4 L100 2"
                        fill="none"
                        stroke={ACCENT}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 0.6,
                        mt: 0.5,
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: 800,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        +24.8%
                      </Box>
                      <Box sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                        YoY portfolio
                      </Box>
                    </Box>
                  </Box>
                </motion.div>

                {/* Mobile stat row (under image) */}
                <Box
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    gap: 1.5,
                    mt: 2.5,
                    justifyContent: 'center',
                  }}
                >
                  {[
                    { value: '10+', label: 'Years', color: ACCENT, Icon: EmojiEventsIcon },
                    { value: '5K+', label: 'Clients', color: '#3b82f6', Icon: GroupsIcon },
                    { value: '95%', label: 'Retention', color: '#16a34a', Icon: TrendingUpIcon },
                  ].map(({ value, label, color, Icon }) => (
                    <Box
                      key={label}
                      sx={{
                        flex: 1,
                        background: '#fff',
                        borderRadius: 2,
                        p: 1.4,
                        textAlign: 'center',
                        boxShadow:
                          '0 10px 24px -8px rgba(11,36,71,0.15)',
                        border: '1px solid rgba(11,36,71,0.06)',
                      }}
                    >
                      <Box
                        sx={{
                          color,
                          display: 'flex',
                          justifyContent: 'center',
                          mb: 0.5,
                          '& svg': { fontSize: '1.1rem' },
                        }}
                      >
                        <Icon />
                      </Box>
                      <Box
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: 800,
                          color: NAVY,
                          lineHeight: 1,
                        }}
                      >
                        {value}
                      </Box>
                      <Box
                        sx={{
                          fontSize: '0.7rem',
                          color: '#64748b',
                          fontWeight: 600,
                          mt: 0.3,
                        }}
                      >
                        {label}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* Content column */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7 }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 1.6,
                  py: 0.5,
                  mb: 2.5,
                  borderRadius: 5,
                  background: 'rgba(239,106,29,0.1)',
                  border: '1px solid rgba(239,106,29,0.25)',
                  color: ACCENT,
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  letterSpacing: 0.6,
                }}
              >
                <VerifiedIcon sx={{ fontSize: '0.95rem' }} />
                ABOUT US
              </Box>

              <Box
                component="h2"
                sx={{
                  m: 0,
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  fontWeight: 800,
                  color: NAVY,
                  lineHeight: 1.15,
                  letterSpacing: -0.5,
                  mb: 2.5,
                }}
              >
                Equity & Index Research,{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  done right.
                </Box>
              </Box>

              {/* Quote-style highlight */}
              <Box
                sx={{
                  position: 'relative',
                  mb: 3,
                  pl: 3,
                  py: 1,
                  borderLeft: `3px solid ${ACCENT}`,
                }}
              >
                <FormatQuoteIcon
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: -14,
                    color: ACCENT,
                    background: '#fff',
                    fontSize: '1.4rem',
                    opacity: 0.3,
                  }}
                />
                <Box
                  sx={{
                    fontSize: '1rem',
                    color: NAVY,
                    fontWeight: 600,
                    fontStyle: 'italic',
                    lineHeight: 1.55,
                  }}
                >
                  Helping investors build wealth through thoroughly researched
                  decisions — every single trade.
                </Box>
              </Box>

              <Box
                sx={{
                  fontSize: { xs: '0.95rem', md: '1.02rem' },
                  color: '#475569',
                  lineHeight: 1.8,
                  mb: 3.5,
                }}
              >
                <strong style={{ color: NAVY }}>Real Stock Experts</strong> is
                a Mumbai-based, SEBI-registered Research Analyst providing
                reliable and insightful stock & index recommendations across
                the Indian equity market — built around your goals and risk
                profile.
              </Box>

              {/* Differentiators */}
              <Stack spacing={1.6} sx={{ mb: 4 }}>
                {differentiators.map(({ Icon, title, desc }, i) => (
                  <motion.div
                    key={title}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.08 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1.6,
                        p: 1.6,
                        borderRadius: 2,
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          background: 'rgba(239,106,29,0.04)',
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 42,
                          height: 42,
                          borderRadius: 1.5,
                          background:
                            'linear-gradient(135deg, rgba(239,106,29,0.15) 0%, rgba(239,106,29,0.04) 100%)',
                          color: ACCENT,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid rgba(239,106,29,0.18)',
                          '& svg': { fontSize: '1.25rem' },
                        }}
                      >
                        <Icon />
                      </Box>
                      <Box>
                        <Box
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 700,
                            color: NAVY,
                            mb: 0.3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.6,
                          }}
                        >
                          {title}
                          <CheckCircleIcon
                            sx={{ fontSize: '1rem', color: '#22c55e' }}
                          />
                        </Box>
                        <Box
                          sx={{
                            fontSize: '0.9rem',
                            color: '#64748b',
                            lineHeight: 1.55,
                          }}
                        >
                          {desc}
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                ))}
              </Stack>

              {/* CTAs */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
              >
                <Button
                  size="large"
                  onClick={() => navigate('/contact')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: ACCENT,
                    color: '#fff',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 3.2,
                    py: 1.3,
                    fontSize: '1rem',
                    borderRadius: 2,
                    boxShadow: '0 10px 24px rgba(239,106,29,0.4)',
                    '&:hover': {
                      background: ACCENT_HOVER,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 14px 28px rgba(239,106,29,0.5)',
                    },
                    transition: 'all 0.25s ease',
                  }}
                >
                  Get In Touch
                </Button>
                <Button
                  size="large"
                  onClick={() => navigate('/service')}
                  sx={{
                    background: 'transparent',
                    color: NAVY,
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 3.2,
                    py: 1.3,
                    fontSize: '1rem',
                    borderRadius: 2,
                    border: `1.5px solid ${NAVY}`,
                    '&:hover': {
                      background: NAVY,
                      color: '#fff',
                      borderColor: NAVY,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.25s ease',
                  }}
                >
                  Our Services
                </Button>
              </Stack>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default About;
